import React, { useState, useEffect } from 'react';
import { getOCELMetadata } from '../../../services/apiClient';

/**
 * ActivityNodeFilterControl - Control for activity node filtering with match type
 *
 * Similar to ActivityFilterControl but includes match type (any/all) for complex filtering.
 * Allows selecting activities with include/exclude modes and any/all matching.
 *
 * @param {object} value - Current filters { enabled, nodes, matchType, mode }
 * @param {Function} onChange - Callback when filters change
 * @param {string} activeFile - Current active file for fetching metadata
 * @param {boolean} disabled - Whether control is disabled
 */
const ActivityNodeFilterControl = ({
  value = { enabled: false, nodes: [], matchType: 'any', mode: 'include' },
  onChange,
  activeFile,
  disabled = false
}) => {
  const [availableActivities, setAvailableActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch available activities from metadata
  useEffect(() => {
    const fetchActivities = async () => {
      if (!activeFile) return;

      setLoading(true);
      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log('📊 ActivityNodeFilterControl - Fetched metadata:', metadata);

        if (metadata?.event_types) {
          let activities = metadata.event_types;

          // Handle different metadata structures
          if (Array.isArray(activities)) {
            // If activities are objects with {name, attributes}, extract names
            if (activities.length > 0 && typeof activities[0] === 'object' && activities[0].name) {
              activities = activities.map(act => act.name);
            }
          } else if (typeof activities === 'object') {
            // If it's an object, use keys
            activities = Object.keys(activities);
          }

          setAvailableActivities(activities);
          console.log(`✅ ActivityNodeFilterControl - Loaded ${activities.length} activities`);
        }
      } catch (err) {
        console.error('❌ ActivityNodeFilterControl - Failed to fetch activities:', err);
        setAvailableActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [activeFile]);

  const handleEnabledChange = (enabled) => {
    if (disabled) return;
    onChange({ ...value, enabled });
  };

  const handleMatchTypeChange = (matchType) => {
    if (disabled) return;
    onChange({ ...value, matchType });
  };

  const handleModeChange = (mode) => {
    if (disabled) return;
    onChange({ ...value, mode });
  };

  const handleToggleActivity = (activity) => {
    if (disabled) return;

    const newNodes = value.nodes.includes(activity)
      ? value.nodes.filter(a => a !== activity)
      : [...value.nodes, activity];

    onChange({ ...value, nodes: newNodes });
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange({ ...value, nodes: [...availableActivities] });
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange({ ...value, nodes: [] });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Activity Node Filters</label>
        <div className="text-sm text-gray-500 italic">Loading activities...</div>
      </div>
    );
  }

  if (availableActivities.length === 0 && !loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Activity Node Filters</label>
        <div className="text-sm text-gray-500 italic">No activities available</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with Enable Toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Activity Node Filters
          {value.enabled && value.nodes.length > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              ({value.nodes.length} selected)
            </span>
          )}
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={disabled}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Enabled</span>
        </label>
      </div>

      {/* Mode and Match Type Selection */}
      <div className="space-y-2">
        <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
          <span className="text-xs font-medium text-gray-700">Mode:</span>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="activity-node-mode"
              value="include"
              checked={value.mode === 'include'}
              onChange={() => handleModeChange('include')}
              disabled={disabled || !value.enabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Include</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="activity-node-mode"
              value="exclude"
              checked={value.mode === 'exclude'}
              onChange={() => handleModeChange('exclude')}
              disabled={disabled || !value.enabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Exclude</span>
          </label>
        </div>

        <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
          <span className="text-xs font-medium text-gray-700">Match:</span>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="activity-node-match"
              value="any"
              checked={value.matchType === 'any'}
              onChange={() => handleMatchTypeChange('any')}
              disabled={disabled || !value.enabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Any</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="activity-node-match"
              value="all"
              checked={value.matchType === 'all'}
              onChange={() => handleMatchTypeChange('all')}
              disabled={disabled || !value.enabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">All</span>
          </label>
        </div>
      </div>

      {/* Activity List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600">
            Activities to {value.mode === 'include' ? 'Include' : 'Exclude'}
            {value.nodes.length > 0 && (
              <span className="ml-1 text-gray-500">({value.nodes.length} selected)</span>
            )}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={disabled || !value.enabled || value.nodes.length === availableActivities.length}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              disabled={disabled || !value.enabled || value.nodes.length === 0}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 bg-white">
          {availableActivities.map((activity) => (
            <label
              key={activity}
              className={`flex items-center px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer ${
                disabled || !value.enabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={value.nodes.includes(activity)}
                onChange={() => handleToggleActivity(activity)}
                disabled={disabled || !value.enabled}
                className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900">{activity}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Help Text */}
      {!value.enabled && (
        <div className="text-xs text-gray-500 italic">
          Enable activity node filtering to select activities
        </div>
      )}
      {value.enabled && value.nodes.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No activities selected
        </div>
      )}
      {value.enabled && value.nodes.length > 0 && (
        <div className="text-xs text-gray-600">
          <strong>Match {value.matchType}</strong> of the selected activities ({value.mode === 'include' ? 'include' : 'exclude'} matching traces)
        </div>
      )}
    </div>
  );
};

export default ActivityNodeFilterControl;
